import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Grid,
  Paper,
  Divider,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { ArrowBack, ArrowBackIos, ArrowForwardIos, ExpandMore } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDeckDetail } from '../hooks/useDeckDetail';
import { useAnalysis, useSlideDetail } from '../hooks/useAnalysis';
import { LoadingOverlay } from '../components/common/LoadingOverlay';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { FindingsList } from '../components/analysis/FindingsList';
import { ChatInterface } from '../components/chat/ChatInterface';
import { SlideSummary } from '../types';

export type Severity = 'critical' | 'high' | 'low';

export const normalizeSeverity = (value: string | undefined): Severity | null => {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v === 'major') return 'high';
  if (v === 'minor') return 'low';
  if (v === 'critical' || v === 'high' || v === 'low') return v as Severity;
  return null;
};

export const IssueBrowserPage: React.FC = () => {
  const { deckId } = useParams<{ deckId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const normalizedSeverity = useMemo(() => normalizeSeverity(searchParams.get('s') || undefined), [searchParams]);
  const { deck, loading: deckLoading, error: deckError } = useDeckDetail(deckId!);
  const { slides, loading: slidesLoading, error: slidesError } = useAnalysis(deckId!);

  const [selectedSlideNumber, setSelectedSlideNumber] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const slideRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Helper function to check if a slide has issues of a given severity
  function hasSeverity(s: SlideSummary, sev: Severity): boolean {
    switch (sev) {
      case 'critical':
        return (s.criticalCount || 0) > 0;
      case 'high':
        return (s.highCount || 0) > 0;
      case 'low':
        return (s.lowCount || 0) > 0;
    }
  }

  // Select first slide with matching severity when slides load or severity changes
  useEffect(() => {
    if (!slides || slides.length === 0) return;
    
    console.log('[IssueBrowser] Slide selection effect triggered');
    console.log('[IssueBrowser] - Severity filter:', normalizedSeverity);
    console.log('[IssueBrowser] - Currently selected:', selectedSlideNumber);
    console.log('[IssueBrowser] - Total slides:', slides.length);
    console.log('[IssueBrowser] - Slides data:', slides.map(s => ({
      num: s.slideNumber,
      critical: s.criticalCount,
      high: s.highCount,
      low: s.lowCount
    })));
    
    // On initial load (selectedSlideNumber is null), always select based on severity
    const shouldSelect = selectedSlideNumber == null;
    
    if (shouldSelect) {
      if (normalizedSeverity) {
        // Find first slide with the selected severity
        console.log('[IssueBrowser] Looking for slide with severity:', normalizedSeverity);
        const slideWithSeverity = slides.find((s) => {
          const has = hasSeverity(s, normalizedSeverity);
          console.log(`[IssueBrowser]   - Slide ${s.slideNumber}: has ${normalizedSeverity}? ${has}`);
          return has;
        });
        console.log('[IssueBrowser] Found slide with severity:', slideWithSeverity?.slideNumber);
        if (slideWithSeverity) {
          console.log('[IssueBrowser] ✓ Setting selected slide to:', slideWithSeverity.slideNumber);
          setSelectedSlideNumber(slideWithSeverity.slideNumber);
        } else {
          // Fallback to first slide if no slide has the selected severity
          console.log('[IssueBrowser] ✗ No slide with severity, defaulting to first slide:', slides[0].slideNumber);
          setSelectedSlideNumber(slides[0].slideNumber);
        }
      } else {
        // No severity filter, select first slide
        console.log('[IssueBrowser] No severity filter, selecting first slide:', slides[0].slideNumber);
        setSelectedSlideNumber(slides[0].slideNumber);
      }
    } else {
      console.log('[IssueBrowser] Skipping selection - slide already selected:', selectedSlideNumber);
    }
  }, [slides, normalizedSeverity]);

  const { slide, loading: slideLoading, error: slideError, refetch: refetchSlide } = useSlideDetail(
    deckId!,
    selectedSlideNumber ?? 1
  );

  const initialLoading = deckLoading || slidesLoading;
  const error = deckError || slidesError;

  // Full slide list (no severity filtering)
  const fullSlides = slides || [];

  // Ensure selected slide is always valid when slides list changes
  // But don't interfere with initial selection (when selectedSlideNumber transitions from null)
  useEffect(() => {
    if (fullSlides.length === 0) {
      setSelectedSlideNumber(null);
      return;
    }
    
    // Skip validation if selectedSlideNumber is null - let the initial selection effect handle it
    if (selectedSlideNumber === null) {
      console.log('[IssueBrowser] Validation check - skipping (selectedSlideNumber is null, initial selection in progress)');
      return;
    }
    
    const exists = fullSlides.some((s) => s.slideNumber === selectedSlideNumber);
    console.log('[IssueBrowser] Validation check - selected:', selectedSlideNumber, 'exists:', exists);
    if (!exists) {
      console.log('[IssueBrowser] Selected slide does not exist, resetting to first:', fullSlides[0].slideNumber);
      setSelectedSlideNumber(fullSlides[0].slideNumber);
    }
  }, [fullSlides, selectedSlideNumber]);

  // Scroll selected slide into view in the sidebar
  useEffect(() => {
    if (selectedSlideNumber && slideRefs.current[selectedSlideNumber]) {
      const slideElement = slideRefs.current[selectedSlideNumber];
      slideElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedSlideNumber]);

  const currentIndex = useMemo(() => {
    return fullSlides.findIndex((s) => s.slideNumber === selectedSlideNumber);
  }, [fullSlides, selectedSlideNumber]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setSelectedSlideNumber(fullSlides[currentIndex - 1].slideNumber);
    }
  };

  const handleNext = () => {
    if (currentIndex >= 0 && currentIndex < fullSlides.length - 1) {
      setSelectedSlideNumber(fullSlides[currentIndex + 1].slideNumber);
    }
  };

  const findingsForSlide = useMemo(() => {
    if (!slide) return [] as any[];
    return slide.findings || [];
  }, [slide]);

  // Group by severity for accordion
  const groupedFindings = useMemo(() => {
    const groups: Record<Severity, any[]> = { critical: [], high: [], low: [] };
    findingsForSlide.forEach((f: any) => {
      const sev = (normalizeSeverity(String(f.severity)) || 'low') as Severity;
      groups[sev].push(f);
    });
    return groups;
  }, [findingsForSlide]);

  // Expand only the severity provided in ?s= by default; allow multi-expand thereafter
  const [expandedGroups, setExpandedGroups] = useState<Record<Severity, boolean>>({
    critical: false, high: false, low: false,
  });
  useEffect(() => {
    const base: Record<Severity, boolean> = { critical: false, high: false, low: false };
    if (normalizedSeverity) base[normalizedSeverity] = true;
    setExpandedGroups(base);
  }, [normalizedSeverity]);

  // Sidebar background color from highest severity
  const getSlideBackgroundColor = (s: SlideSummary): string => {
    if ((s.criticalCount || 0) > 0) return 'rgba(211, 65, 12, 0.15)'; // Critical - red tint
    if ((s.highCount || 0) > 0) return 'rgba(255, 152, 0, 0.15)'; // High - orange tint
    if ((s.lowCount || 0) > 0) return 'rgba(28, 94, 153, 0.15)'; // Low - blue tint
    return 'transparent';
  };

  if (initialLoading) {
    return <LoadingOverlay open={true} message="Loading issues..." />;
  }

  if (error) {
    return (
      <Box sx={{ py: 1.5, px: 2 }}>
        <ErrorDisplay error={error} />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1.5 }}>
      <Box mb={1.5} sx={{ px: 2 }}>
        <Breadcrumbs sx={{ mb: 1.5 }}>
          <Link underline="hover" color="inherit" onClick={() => navigate('/decks')} sx={{ cursor: 'pointer' }}>
            My Presentations
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/analysis/${deckId}`)}
            sx={{ cursor: 'pointer' }}
          >
            {deck?.fileName || 'Deck'}
          </Link>
          <Typography color="text.primary">Issues</Typography>
        </Breadcrumbs>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(`/analysis/${deckId}`)}>
              Back to Analysis
            </Button>
            <Typography variant="h4" fontWeight={600}>Issues</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={handlePrevious} disabled={currentIndex <= 0} size="small">
              <ArrowBackIos />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {fullSlides.length > 0 ? `${currentIndex + 1} / ${fullSlides.length}` : '0 / 0'}
            </Typography>
            <IconButton onClick={handleNext} disabled={currentIndex >= fullSlides.length - 1 || fullSlides.length === 0} size="small">
              <ArrowForwardIos />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', display: 'flex', gap: 2 }}>
        {/* Sidebar - Slide thumbnails list */}
        <Box sx={{ width: { xs: '100%', lg: 'calc(16.67% - 110px)' }, minWidth: '150px', flexShrink: 0, height: '600px' }}>
          <Paper sx={{ p: 1.5, height: '100%', overflow: 'auto' }}>
            <Box display="flex" flexDirection="column" gap={0.75}>
              {fullSlides.map((s) => {
                const isSelected = s.slideNumber === selectedSlideNumber;
                const backgroundColor = getSlideBackgroundColor(s);
                return (
                  <Box
                    key={s.slideNumber}
                    ref={(el) => {
                      if (el instanceof HTMLElement) {
                        slideRefs.current[s.slideNumber] = el as HTMLDivElement;
                      }
                    }}
                    onClick={() => setSelectedSlideNumber(s.slideNumber)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 0.5,
                      p: 0.75,
                      cursor: 'pointer',
                      borderRadius: 1,
                      border: isSelected ? '2px solid' : '2px solid transparent',
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                      bgcolor: backgroundColor,
                      '&:hover': { bgcolor: backgroundColor === 'transparent' ? 'action.hover' : backgroundColor },
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: 140,
                        height: 84,
                        borderRadius: 1,
                        bgcolor: 'grey.100',
                        boxShadow: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {s.imageUrl ? (
                        <img
                          src={s.imageUrl}
                          alt={`Slide ${s.slideNumber}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          loading="lazy"
                        />
                      ) : null}
                    </Box>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ display: 'block' }} 
                      noWrap
                    >
                      Slide {s.slideNumber}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Box>

        {/* Tabbed Content Area */}
        <Box sx={{ flex: 1, height: '600px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {/* Tab Headers */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5, position: 'relative', zIndex: 2 }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab label="Issues" />
              <Tab label="Chat" />
            </Tabs>
          </Box>

          {/* Chat Interface - Always rendered but positioned/hidden based on active tab */}
          <Box
            sx={{
              position: 'absolute',
              top: '64px', // Below tab headers (48px) + margin-bottom (16px)
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: activeTab === 1 ? 1 : -1,
              visibility: activeTab === 1 ? 'visible' : 'hidden',
              pointerEvents: activeTab === 1 ? 'auto' : 'none',
            }}
          >
            {fullSlides.length > 0 && (
              <Grid container spacing={2} sx={{ height: '100%' }}>
                {/* Left Pane - Slide Details (only show when slide is selected) */}
                <Grid item xs={12} lg={6} sx={{ height: '100%' }}>
                  {selectedSlideNumber == null ? (
                    <Paper sx={{ p: 2, height: '100%', overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="text.secondary">Select a slide to view details.</Typography>
                    </Paper>
                  ) : !slide ? (
                    <Paper sx={{ p: 2, height: '100%', overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="text.secondary">Loading slide...</Typography>
                    </Paper>
                  ) : (
                    <Paper sx={{ p: 2, height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                      <Box>
                        <Typography variant="h5" gutterBottom>
                          Slide {slide.slideNumber}
                        </Typography>
                        {slide.slideTitle && (
                          <Typography 
                            variant="h6" 
                            color="text.secondary" 
                            gutterBottom
                            title={slide.slideTitle}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {slide.slideTitle}
                          </Typography>
                        )}
                      </Box>

                      {slide.imageUrl && (
                        <Box sx={{ mb: 2, mt: 1.5 }}>
                          <Box
                            component="img"
                            src={slide.imageUrl}
                            alt={`Slide ${slide.slideNumber}`}
                            sx={{
                              width: '100%',
                              height: 'auto',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              boxShadow: 2,
                            }}
                          />
                        </Box>
                      )}

                      {slide.extractedText && (
                        <>
                          <Divider sx={{ my: 1.5 }} />
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Extracted Text
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'pre-wrap', bgcolor: 'background.default', p: 1.5, borderRadius: 1 }}
                          >
                            {slide.extractedText}
                          </Typography>
                        </>
                      )}

                      {/* Slide Issues/Findings */}
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Slide Issues
                      </Typography>
                      <FindingsList
                        findings={slide.findings || []}
                        deckId={deckId!}
                        slideNumber={slide.slideNumber}
                        onAddressedChange={refetchSlide}
                      />
                    </Paper>
                  )}
                </Grid>

                {/* Right Pane - Chat Interface (always rendered) */}
                <Grid item xs={12} lg={6} sx={{ height: '100%' }}>
                  <ChatInterface 
                    deckId={deckId!} 
                    slideNumber={selectedSlideNumber ?? undefined} 
                  />
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Tab Panels */}
          <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
            {/* Issues Tab */}
            <Box
              sx={{
                display: activeTab === 0 ? 'block' : 'none',
                height: '100%',
              }}
            >
              <Grid container spacing={2} sx={{ height: '100%' }}>
                {/* Middle Pane - Single slide view */}
                <Grid item xs={12} lg={6} sx={{ height: '100%' }}>
                  <Paper sx={{ p: 2, height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                    {fullSlides.length === 0 ? (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography color="text.secondary">No slides available.</Typography>
                      </Box>
                    ) : !slide ? (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography color="text.secondary">Loading slide...</Typography>
                      </Box>
                    ) : (
                      <>
                        <Box>
                          <Typography variant="h5" gutterBottom>
                            Slide {slide.slideNumber}
                          </Typography>
                          {slide.slideTitle && (
                            <Typography 
                              variant="h6" 
                              color="text.secondary" 
                              gutterBottom
                              title={slide.slideTitle}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {slide.slideTitle}
                            </Typography>
                          )}
                        </Box>

                        {slide.imageUrl && (
                          <Box sx={{ mb: 2, mt: 1.5 }}>
                            <Box
                              component="img"
                              src={slide.imageUrl}
                              alt={`Slide ${slide.slideNumber}`}
                              sx={{
                                width: '100%',
                                height: 'auto',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: 2,
                              }}
                            />
                          </Box>
                        )}

                        {slide.extractedText && (
                          <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              Extracted Text
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ whiteSpace: 'pre-wrap', bgcolor: 'background.default', p: 1.5, borderRadius: 1 }}
                            >
                              {slide.extractedText}
                            </Typography>
                          </>
                        )}
                      </>
                    )}
                  </Paper>
                </Grid>

                {/* Right Pane - Findings grouped by severity */}
                <Grid item xs={12} lg={6} sx={{ height: '100%' }}>
                  <Paper sx={{ p: 2, height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                    {selectedSlideNumber == null ? (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography color="text.secondary">Select a slide to view its issues.</Typography>
                      </Box>
                    ) : !slide ? (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography color="text.secondary">Slide data not available.</Typography>
                      </Box>
                    ) : findingsForSlide.length === 0 ? (
                      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" gap={2}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: 'success.lighter',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="h2" color="success.main">✓</Typography>
                        </Box>
                        <Typography variant="h6" fontWeight={600} color="success.main">
                          No Issues Found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 300 }}>
                          This slide looks good! No issues were detected during analysis.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {(['critical','high','low'] as Severity[]).map((sev) => {
                          const items = groupedFindings[sev] || [];
                          if (items.length === 0) return null;
                          const colorMap: Record<Severity, 'error'|'warning'|'info'|'success'> = {
                            critical: 'error', high: 'warning', low: 'info',
                          };
                          const label = sev.charAt(0).toUpperCase() + sev.slice(1);
                          return (
                            <Accordion
                              key={sev}
                              expanded={!!expandedGroups[sev]}
                              onChange={() => setExpandedGroups((prev) => ({ ...prev, [sev]: !prev[sev] }))}
                              sx={{ mb: 1, '&:before': { display: 'none' }, borderLeft: 4, borderColor: `${colorMap[sev]}.main` }}
                            >
                              <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Chip size="small" color={colorMap[sev]} label={label} />
                                  <Typography variant="body2" color="text.secondary">
                                    {items.length} issue{items.length > 1 ? 's' : ''}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <FindingsList
                                  findings={items}
                                  deckId={deckId!}
                                  slideNumber={slide.slideNumber}
                                  onAddressedChange={refetchSlide}
                                />
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      {slideLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label="Loading slide"
        >
          <CircularProgress />
        </Box>
      )}
      </Box>
    </Box>
  );
};

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default IssueBrowserPage;


